/** Build export payloads and trigger browser downloads for advisor chat transcripts. */

const slugifyFilename = (title) =>
  String(title || "conversation")
    .trim()
    .slice(0, 48)
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase() || "conversation";

const messagePlainText = (msg) => {
  const raw = typeof msg.content === "string" ? msg.content : String(msg.content ?? "");
  if (msg.role === "assistant" && msg.isHTML) {
    try {
      const tmp = document.createElement("div");
      tmp.innerHTML = raw;
      return (tmp.textContent || tmp.innerText || "").trim();
    } catch (_) {
      return raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }
  }
  return raw.trim();
};

const exportRole = (role) => (role === "assistant" ? "advisor" : "user");

const normalizeDashboardPicker = (picker) => {
  if (!picker || typeof picker !== "object") return null;
  const dashboards = Array.isArray(picker.dashboards)
    ? picker.dashboards
        .filter((d) => d && d.id != null)
        .map((d) => ({
          id: d.id,
          name: d.name,
          ...(d.group_id != null ? { group_id: d.group_id } : {}),
        }))
    : [];
  if (!dashboards.length) return null;
  const groups = Array.isArray(picker.groups)
    ? picker.groups
        .filter((g) => g && g.id != null)
        .map((g) => ({
          id: g.id,
          name: g.name,
          ...(g.color != null ? { color: g.color } : {}),
        }))
    : [];
  const normalized = { dashboards, groups };
  if (picker.default_dashboard_id != null) {
    normalized.default_dashboard_id = picker.default_dashboard_id;
  }
  return normalized;
};

const normalizeDashboardSelection = (selection) => {
  if (!selection || typeof selection !== "object" || selection.id == null) {
    return null;
  }
  const name =
    typeof selection.name === "string" && selection.name.trim()
      ? selection.name.trim()
      : null;
  return {
    id: selection.id,
    ...(name ? { name } : {}),
  };
};

const normalizeExportMessage = (msg, index) => {
  const content = messagePlainText(msg);
  if (!content && !msg.dashboardPicker) return null;
  const ts = typeof msg.ts === "number" ? msg.ts : Date.now();
  const entry = {
    id: `msg_${ts}_${index}`,
    role: exportRole(msg.role),
    content: content || "",
    timestamp: new Date(ts).toISOString(),
  };

  if (msg.streaming) {
    entry.in_progress = true;
  }

  if (Array.isArray(msg.suggestedFollowups) && msg.suggestedFollowups.length) {
    entry.suggested_followups = msg.suggestedFollowups
      .filter((item) => typeof item === "string" && item.trim())
      .map((item) => item.trim());
  }

  if (typeof msg.guidance === "string" && msg.guidance.trim()) {
    entry.guidance = messagePlainText({ ...msg, content: msg.guidance, isHTML: true });
  }

  if (typeof msg.reasoning === "string" && msg.reasoning.trim()) {
    entry.reasoning = msg.reasoning.trim();
  }

  if (Array.isArray(msg.toolActions) && msg.toolActions.length) {
    entry.tool_actions = msg.toolActions
      .filter((item) => typeof item === "string" && item.trim())
      .map((item) => item.trim());
  }

  const dashboardPicker = normalizeDashboardPicker(msg.dashboardPicker);
  if (dashboardPicker) {
    entry.dashboard_picker = dashboardPicker;
  }

  const dashboardSelection = normalizeDashboardSelection(msg.dashboardSelection);
  if (dashboardSelection) {
    entry.dashboard_selection = dashboardSelection;
  }

  if (
    !entry.content &&
    !entry.dashboard_picker &&
    !entry.dashboard_selection &&
    !entry.suggested_followups?.length &&
    !entry.reasoning
  ) {
    return null;
  }

  return entry;
};

const normalizeMessages = (messages) =>
  (messages || [])
    .filter((msg) => msg && (msg.role === "user" || msg.role === "assistant"))
    .filter(
      (msg) =>
        !msg.streaming ||
        messagePlainText(msg) ||
        msg.dashboardPicker ||
        msg.reasoning ||
        (Array.isArray(msg.suggestedFollowups) && msg.suggestedFollowups.length)
    )
    .map((msg, index) => normalizeExportMessage(msg, index))
    .filter(Boolean);

export const buildConversationExportJson = ({
  conversationId,
  title,
  messages,
}) => ({
  exported_at: new Date().toISOString(),
  conversation_id: conversationId || "",
  title: title || "New chat",
  messages: normalizeMessages(messages),
});

const formatMarkdownTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (_) {
    return "";
  }
};

export const buildConversationExportMarkdown = ({
  title,
  messages,
}) => {
  const normalized = normalizeMessages(messages);
  const exportDate = new Date().toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const lines = [
    `# Sonic Advisor — ${title || "New chat"}`,
    `Exported: ${exportDate}`,
    "",
    "---",
    "",
  ];

  normalized.forEach((msg) => {
    const label = msg.role === "user" ? "You" : "Advisor";
    const time = formatMarkdownTime(msg.timestamp);
    lines.push(`**${label}** · ${time}`);
    if (msg.content) {
      lines.push(msg.content);
    }
    if (msg.dashboard_selection) {
      lines.push("");
      const label = msg.dashboard_selection.name || `Dashboard ${msg.dashboard_selection.id}`;
      lines.push(`**Dashboard selected:** ${label} (id: ${msg.dashboard_selection.id})`);
    }
    if (typeof msg.reasoning === "string" && msg.reasoning.trim()) {
      lines.push("");
      lines.push("**Reasoning:**");
      lines.push(msg.reasoning.trim());
    }
    if (Array.isArray(msg.tool_actions) && msg.tool_actions.length) {
      lines.push("");
      lines.push("**Tool actions:**");
      msg.tool_actions.forEach((tool) => lines.push(`- ${tool}`));
    }
    if (msg.in_progress) {
      lines.push("");
      lines.push("_*(in progress at export time)*_");
    }
    if (Array.isArray(msg.suggested_followups) && msg.suggested_followups.length) {
      lines.push("");
      lines.push("**Suggested follow-ups:**");
      msg.suggested_followups.forEach((q) => lines.push(`- ${q}`));
    }
    if (typeof msg.guidance === "string" && msg.guidance.trim()) {
      lines.push("");
      lines.push(msg.guidance.trim());
    }
    if (msg.dashboard_picker?.dashboards?.length) {
      lines.push("");
      lines.push("**Dashboards:**");
      msg.dashboard_picker.dashboards.forEach((d) => {
        lines.push(`- ${d.name} (id: ${d.id})`);
      });
    }
    lines.push("");
  });

  return lines.join("\n").trimEnd() + "\n";
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadConversationJson = ({ conversationId, title, messages }) => {
  const payload = buildConversationExportJson({ conversationId, title, messages });
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const date = new Date().toISOString().slice(0, 10);
  triggerDownload(blob, `pivony-advisor-${slugifyFilename(title)}-${date}.json`);
};

export const downloadConversationMarkdown = ({ title, messages }) => {
  const markdown = buildConversationExportMarkdown({ title, messages });
  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
  const date = new Date().toISOString().slice(0, 10);
  triggerDownload(blob, `pivony-advisor-${slugifyFilename(title)}-${date}.md`);
};
